/** 
 * A static class that maintains a Map of all keys being pressed and exposes an interface for other code to respond to key presses.
 */
export default class KeyListener
{
    private static keyState = new Map<Number, Boolean>(); 

    private static handlers = new Set<Function>();

    private static hasInit = false;
    
    /**
     * Establishes a global keydown and keyup event handler for use by KeyListener.
     * Be very careful calling this more than once with forceInit set to true!
     * TODO: maybe refactor to use `code` instead of `keyCode`.
     */
    public static init(forceInit : Boolean = false)
    {
        if (!this.hasInit || forceInit)
        {
            this.hasInit = true;
            window.addEventListener('keydown', (e : KeyboardEvent) =>
            {
                this.keyState.set(e.keyCode, true);
                const typing = this.isTypingInTextBox();
                if (!(this.shouldIgnoreBasedOnModKeys(e)))
                {
                    this.handlers.forEach((handler : Function) =>
                    {
                        handler(e, typing);
                    });
                }
            }, true);    
            window.addEventListener('keyup', (e : KeyboardEvent) =>
            {
                this.keyState.set(e.keyCode, false);
            }, true);
        }
    }

    /**
     * Clears the key state so that no key appears to be down.
     * Useful for getting out of situations where the keyup event was erroneously lost.
     */
    public static reset()
    {
        this.keyState.clear();
    }

    /**
     * Pass an object with a member function called `keyHandler`.
     * The `keyHandler` function will get called on every keydown event processed by KeyListener.
     * The `keyHandler` function gets passed the `KeyboardEvent` event and a boolean `isTextBoxFocused` 
     * so the handler can filter textbox input.
     * `addKeyHandler` does null checking and avoids multiple adds automatically.
     */
    public static addKeyHandler(a : any)
    {
        if (a.keyHandler != null && !(KeyListener.handlers.has(a.keyHandler)))
        {
            KeyListener.handlers.add(a.keyHandler);
        }
    }

    /**
     * Pass an object with a member function called keyHandler.
     * The function will no longer get called on every keydown event processed by KeyListener.
     * removeKeyHandler does null checking and avoids removing a handler that wasn't previously added.
     */
    public static removeKeyHandler(a : any)
    {
        if (a.keyHandler != null && KeyListener.handlers.has(a.keyHandler))
        {
            KeyListener.handlers.delete(a.keyHandler);
        }
    }

    /**
     * Use https://keycode.info/ to figure out what number to put here.
     * Note that there are a bunch of more specific KeyListener functions with the key code specified.
     */
    public static isKeyDown(keyCode : Number)
    {
        return this.keyState.get(keyCode);
    }

    public static isArrowLeftDown()
    {
        return this.keyState.get(37);
    }

    public static isArrowRightDown()
    {
        return this.keyState.get(39);
    }

    public static isArrowUpDown()
    {
        return this.keyState.get(38);
    }

    public static isArrowDownDown()
    {
        return this.keyState.get(40);
    }

    public static isShiftDown()
    {
        return this.keyState.get(16);
    }

    public static isCtrlDown()
    {
        return this.keyState.get(17);
    }

    public static isAltDown()
    {
        return this.keyState.get(18);
    }

    public static isTabDown()
    {
        return this.keyState.get(9);
    }

    public static isSpacebarDown()
    {
        return this.keyState.get(32);
    }

    public static isEnterDown()
    {
        return this.keyState.get(13);
    }

    public static isEscDown()
    {
        return this.keyState.get(27);
    }

    public static isADown()
    {
        return this.keyState.get(65);
    }

    public static isBDown()
    {
        return this.keyState.get(66);
    }

    public static isCDown()
    {
        return this.keyState.get(67);
    }

    public static isDDown()
    {
        return this.keyState.get(68);
    }

    public static isEDown()
    {
        return this.keyState.get(69);
    }

    public static isFDown()
    {
        return this.keyState.get(70);
    }

    public static isKDown()
    {
        return this.keyState.get(75);
    }

    public static isNDown()
    {
        return this.keyState.get(78);
    }

    public static isODown()
    {
        return this.keyState.get(79);
    }

    public static isRDown()
    {
        return this.keyState.get(82);
    }

    public static isSDown()
    {
        return this.keyState.get(83);
    }

    public static isVDown()
    {
        return this.keyState.get(86);
    }

    public static isXDown()
    {
        return this.keyState.get(88);
    }

    public static isYDown()
    {
        return this.keyState.get(89);
    }

    public static isZDown()
    {
        return this.keyState.get(90);
    }

    public static isPeriodDown()
    {
        return this.keyState.get(190);
    }

    public static isCommaDown()
    {
        return this.keyState.get(188);
    }

    public static isTypingInTextBox() : boolean
    {
        if (document == null) return false;
        if (document.activeElement == null) return false;
        const tagName = document.activeElement.tagName.toLowerCase();
        if (tagName === 'textarea') return true;
        if (tagName !== 'input') return false;
        let type = document.activeElement.getAttribute('type');
        if (type == null) return false;
        type = type.toLowerCase();
        // if any of these input types is not supported by a browser, it will behave as input type text.
        const inputTypes = ['text', 'password', 'number', 'email', 'tel', 'url', 'search', 'date', 'datetime', 'datetime-local', 'time', 'month', 'week'];
        return inputTypes.indexOf(type) >= 0;
    }

    public static shouldIgnoreBasedOnModKeys(ev : KeyboardEvent) : boolean
    {
        // Alt/Option OR Command OR Ctrl OR Windows key is down
        return ev.altKey || ev.metaKey || ev.ctrlKey || ev.getModifierState("OS");
    }

    public static isAnyKeyDown()
    {
        for(const pressed of this.keyState.values())
        {
            if (pressed) return true;
        }
        return false;
    }
}